import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {object} UpsellOffer
 * @property {number} id - Variant ID of the offered variant
 * @property {string} product - Product title
 * @property {string} variant - Variant title, empty for single-variant products
 * @property {string} image - Image URL, may be empty
 * @property {string} alt - Image alt text
 * @property {string} price - Offer price, formatted
 * @property {string | null} compare - Regular price, formatted, when the offer is cheaper
 * @property {string} save - "Save 20%" style label, empty when there is no discount
 * @property {number | null} limit - How many of the offer can be in the cart before it stops showing, null for no limit
 */

/**
 * Add to cart offer shown under the cart icon.
 *
 * Listens for add-to-cart events in the capture phase, before the cart drawer sees them. When the
 * added variant has an offer (JSON printed by snippets/cart-upsell-offers.liquid inside its product
 * form), the drawer's auto-open is held back and the offer shows instead. Adding the offer or saying
 * no hands over to the drawer as normal.
 */
class CartUpsell extends HTMLElement {
  /** @type {UpsellOffer | null} */
  #offer = null;

  /** Whether the drawer would have opened on this add */
  #drawerWasAutoOpen = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { capture: true });
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#position);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { capture: true });
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#position);
  }

  get #drawer() {
    return document.querySelector('cart-drawer-component');
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (event.action !== 'add' || !(event.target instanceof Element) || this.contains(event.target)) return;

    const variantId = String(event.lines?.[0]?.merchandiseId ?? '').split('/').pop();
    const offer = this.#findOffer(event.target, variantId);
    if (!offer) return;

    // Hold back the drawer's auto-open for this add. The drawer reads the attribute in its own
    // listener, which runs after this capture listener, so put it back once the dispatch is over.
    const drawer = this.#drawer;
    this.#drawerWasAutoOpen = Boolean(drawer?.hasAttribute('auto-open'));
    if (this.#drawerWasAutoOpen) {
      drawer?.removeAttribute('auto-open');
      queueMicrotask(() => drawer?.setAttribute('auto-open', ''));
    }

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        // Hidden once the cart holds as many of the offer as its limit, and back when some are removed
        const inCart = (detail?.items ?? [])
          .filter((/** @type {{variant_id: number}} */ item) => item.variant_id === offer.id)
          .reduce((/** @type {number} */ total, /** @type {{quantity: number}} */ item) => total + item.quantity, 0);
        if (offer.limit != null && inCart >= offer.limit) {
          this.#openDrawer();
          return;
        }

        this.#show(offer);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-upsell] Event promise rejected:', error);
      });
  };

  /**
   * @param {Element} source - The element that added to the cart
   * @param {string} variantId
   * @returns {UpsellOffer | null}
   */
  #findOffer(source, variantId) {
    // The form that added it first, then any other on the page (sticky bars and app buttons
    // add through their own forms)
    const own = source.closest('product-form-component')?.querySelector('[data-cart-upsell-offers]');
    const scripts = [own, ...document.querySelectorAll('[data-cart-upsell-offers]')];

    for (const script of scripts) {
      if (!script?.textContent) continue;

      try {
        const offer = JSON.parse(script.textContent)[variantId];
        if (offer) return offer;
      } catch {
        // Ignore unreadable data and keep looking
      }
    }

    return null;
  }

  /**
   * @param {UpsellOffer} offer
   */
  #show(offer) {
    this.#offer = offer;

    const image = /** @type {HTMLImageElement} */ (this.querySelector('[data-cart-upsell-image]'));
    if (offer.image) {
      image.src = offer.image;
    } else {
      image.removeAttribute('src');
    }
    image.alt = offer.alt;

    this.#setText('[data-cart-upsell-product]', offer.product);
    this.#setText('[data-cart-upsell-variant]', offer.variant);
    this.#setText('[data-cart-upsell-price]', offer.price);
    this.#setText('[data-cart-upsell-compare]', offer.compare ?? '');
    this.#setText('[data-cart-upsell-save]', offer.save);
    this.#setText('[data-cart-upsell-error]', '');
    this.querySelector('[data-cart-upsell-note]')?.toggleAttribute('hidden', !offer.compare);

    const addButton = this.querySelector('[data-cart-upsell-add]');
    addButton?.removeAttribute('aria-busy');

    this.#position();
    if (!this.matches(':popover-open')) this.showPopover();
    if (addButton instanceof HTMLElement) addButton.focus({ preventScroll: true });
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    const element = this.querySelector(selector);
    if (element) element.textContent = text;
  }

  /** Sits under the cart icon, its arrow pointing at the icon */
  #position = () => {
    const icon = document.querySelector('cart-icon');
    const rect = icon instanceof HTMLElement && icon.offsetParent ? icon.getBoundingClientRect() : null;

    if (!rect) {
      this.style.setProperty('--cart-upsell-top', '16px');
      this.style.setProperty('--cart-upsell-right', '16px');
      this.style.setProperty('--cart-upsell-arrow', '-100px');
      return;
    }

    const right = Math.max(16, window.innerWidth - rect.right - 12);
    const arrow = window.innerWidth - right - (rect.left + rect.width / 2) - 7;

    this.style.setProperty('--cart-upsell-top', `${Math.round(rect.bottom + 14)}px`);
    this.style.setProperty('--cart-upsell-right', `${Math.round(right)}px`);
    this.style.setProperty('--cart-upsell-arrow', `${Math.round(arrow)}px`);
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-cart-upsell-dismiss]')) {
      this.#dismiss();
    } else if (event.target.closest('[data-cart-upsell-add]')) {
      this.#add();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#dismiss();
    }
  };

  #dismiss() {
    this.#hide();
    this.#openDrawer();
  }

  #hide() {
    if (this.matches(':popover-open')) this.hidePopover();
    this.#offer = null;
  }

  #openDrawer() {
    if (!this.#drawerWasAutoOpen) return;

    const themeDrawer = /** @type {{ open?: () => void } | null} */ (this.#drawer?.closest('theme-drawer') ?? null);
    themeDrawer?.open?.();
  }

  async #add() {
    const offer = this.#offer;
    const addButton = this.querySelector('[data-cart-upsell-add]');
    if (!offer || addButton?.getAttribute('aria-busy') === 'true') return;

    addButton?.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: offer.id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        addButton?.removeAttribute('aria-busy');
        this.#setText('[data-cart-upsell-error]', data.description || data.message || '');
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

      const deferred = CartLinesUpdateEvent.createPromise();
      this.#hide();

      // Dispatched from the page, not this popover, so the drawer, cart icon and cart items
      // treat it like any other add to cart
      document.body.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(offer.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      addButton?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
